import * as React from "react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Button } from "./button";
import { Upload } from "@/assets";
import { BodyDefault, BodySmall } from "./typography";
import { handleAndNotifyError } from "@/utils/errorHandling";

const IconWrapper = ({
    children,
    className,
    prefixIcon,
    onPrefixClick,
    suffixIcon,
    onSuffixClick,

    ...props
}: React.ComponentProps<"div"> & {
    prefixIcon?: React.ReactNode;
    onPrefixClick?: () => void;
    suffixIcon?: React.ReactNode;
    onSuffixClick?: () => void;
}) => {
    return (
        <div
            className={cn(
                "relative",
                prefixIcon && "[&_input]:pl-widest",
                suffixIcon && "[&_input]:pr-widest",

                className,
            )}
            {...props}
        >
            {!!prefixIcon && (
                <button
                    onClick={onPrefixClick}
                    className={cn(
                        "pl-default text-default-color absolute inset-y-0 flex scale-75 transform items-center",
                        onPrefixClick
                            ? "cursor-pointer"
                            : "pointer-events-none",
                    )}
                >
                    {prefixIcon}
                </button>
            )}
            {children}
            {!!suffixIcon && (
                <button
                    onClick={onSuffixClick}
                    className={cn(
                        "pr-default text-default-color absolute inset-y-0 right-0 flex scale-75 transform items-center",
                        onSuffixClick
                            ? "cursor-pointer"
                            : "pointer-events-none",
                    )}
                >
                    {suffixIcon}
                </button>
            )}
        </div>
    );
};
IconWrapper.displayName = "IconWrapper";

const Input = ({ className, ...props }: React.ComponentProps<"input">) => {
    return (
        <input
            className={cn(
                "px-default rounded-default border-input-default bg-input-default placeholder:text-muted-foreground focus-visible:ring-ring active:bg-input-press hover:not-focus:bg-input-hover file:text-foreground flex w-full border ring-offset-0 ring-inset file:border-0 file:bg-transparent focus-visible:outline-none",
                "disabled:bg-input-disabled disabled:border-input-disabled disabled:text-disabled disabled:cursor-not-allowed",
                "h-default",
                className,
            )}
            {...props}
        />
    );
};
Input.displayName = "Input";

const FileInput = ({
    onChange,
    accept,
    children,
    multiple = false,
    disabled = false,
    ...props
}: React.PropsWithChildren<
    Omit<React.ComponentProps<"input">, "onChange"> & {
        /**
         * The callback function that is called when the user selects files.
         *
         * @param nextFile A single File object when `multiple` is false, or an array of File objects when `multiple` is true.
         */
        onChange: (nextFile: File | File[]) => void;
        multiple?: boolean;
        disabled?: boolean;
    }
>) => {
    const [dragOver, setDragOver] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    /**
     * Check if the file matches the given mime type or extension.
     *
     * @param file The file to validate.
     * @param mimeType The mime type or extension to check against.
     * @returns True if the file matches the mime type or extension, false otherwise.
     */
    const validateFile = (file: File, mimeType: string) => {
        if (mimeType === "image/*") {
            return file.type.startsWith("image/");
        }

        if (mimeType.startsWith(".")) {
            return file.name.toLowerCase().endsWith(mimeType.toLowerCase());
        }

        return file.type === mimeType;
    };

    /**
     * Check if all files match the accepted types.
     *
     * @param files The array of files to validate.
     * @returns True if all files are valid according to the accepted types, false otherwise.
     */
    const validateFiles = (files: File[]) => {
        if (!accept) {
            return true;
        }

        const acceptedTypes = accept.split(",").map((t) => t.trim());

        let valid = true;

        for (const file of files) {
            const isFileValid = acceptedTypes.some((type) =>
                validateFile(file, type),
            );

            if (!isFileValid) {
                handleAndNotifyError(
                    `File ${file.name} is not an accepted file type.`,
                );

                valid = false;
            }
        }

        return valid;
    };

    const handleFiles = (fileList: FileList) => {
        if (fileList.length <= 0) {
            return;
        }

        const files: File[] = [];

        for (const file of fileList) {
            files.push(file);
        }

        if (!validateFiles(files)) {
            return;
        }

        if (!multiple && fileList.length > 1) {
            handleAndNotifyError(
                `Multiple files selected, only the first file will be used: ${fileList[0].name}`,
            );
        }

        if (multiple) {
            onChange(files);
        } else {
            onChange(fileList[0]);
        }
    };

    return (
        <div
            className={cn(
                "rounded-default gap-wide p-wide flex min-h-43.5 flex-col items-center border-solid text-center transition-all",
                disabled
                    ? "bg-input-disabled border-input-disabled text-disabled cursor-not-allowed border"
                    : dragOver
                      ? "bg-selected-hover border-input-selected border"
                      : isFocused
                        ? "bg-input-default border-focus border-2"
                        : "bg-input-default border-input-default border",
            )}
            onDragOver={(e) => {
                if (disabled) return;
                e.preventDefault();
                setDragOver(true);
            }}
            onDragLeave={(e) => {
                if (disabled) return;
                e.preventDefault();
                setDragOver(false);
            }}
            onDrop={(e) => {
                if (disabled) return;
                e.preventDefault();
                setDragOver(false);

                handleFiles(e.dataTransfer.files);
            }}
        >
            <Upload className="h-8 w-8" />
            <div className="gap-narrower flex flex-col">
                {children ?? (
                    <>
                        <BodyDefault>
                            Choose an image or drag and drop it here.
                        </BodyDefault>
                        <BodySmall>
                            PDF, PNG, GIF, JPG or JPEG formats up to 20 MB
                        </BodySmall>
                    </>
                )}
            </div>
            <Button
                onClick={(e) => {
                    e.preventDefault();
                    if (!disabled) {
                        fileInputRef.current?.click();
                    }
                }}
                appearance="neutral"
                disabled={disabled}
            >
                Browse
            </Button>
            <input
                {...props}
                accept={accept}
                multiple={multiple}
                disabled={disabled}
                ref={fileInputRef}
                type="file"
                className={cn("hidden", props.className)}
                onChange={(e) => e.target.files && handleFiles(e.target.files)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                data-testid="file-input"
            />
        </div>
    );
};
FileInput.displayName = "FileInput";

export { IconWrapper, Input, FileInput };
